import { Box, Grid, useMediaQuery, useTheme } from '@mui/material';
import { SvgIconComponent, Today } from '@mui/icons-material';
import _ from 'lodash';
import { useEffect } from 'react';
import { useImmer } from 'use-immer';
import { PermissionBoundary } from '../../../../../core/permissions/permission-boundary.component';
import { ActionButtonSingle } from '../../action-button-single/action-button-single.component';
import { ActionsButton } from '../../actions-button/actions-button.component';
import { LoadedActionsButtonItem } from '../../actions-button/actions-button.types';
import {
  MultiSelectPageControlDropdown,
  PageControlAutoComplete,
  PageControlDatePicker,
  PageControlDropdown,
  PageControlSearchInput,
  PageControlsWrapper
} from '../../page-controls/page-controls.context';
import { useListPageHeaderStyles } from './list-page-header.styles';
import { CheckMyPermissionDto } from '@groundfloor-platform/mgmt-service-g-sdk';

type InputForm = Record<string, any>;

type InputProps<T extends InputForm> =
  | {
      type: 'text';
      label: string;
      field: keyof T;
      isAdvanced?: true;
    }
  | {
      type: 'date-range';
      fromField: keyof T;
      toField: keyof T;
      fromLabel: string;
      toLabel: string;
      isAdvanced?: true;
    }
  | {
      type: 'select';
      options: { label: string; value: string }[];
      label: string;
      field: keyof T;
      isAdvanced?: true;
      halfWidthOnMobile?: boolean;
    }
  | {
      type: 'auto-complete';
      options: { label: string; value: string }[];
      label: string;
      field: keyof T;
      isAdvanced?: true;
      halfWidthOnMobile?: boolean;
      /** For options loaded on first open. */
      onOpen?(): void;
      loading?: boolean;
    }
  | {
      type: 'multi-select';
      options: { label: string; value: string }[];
      label: string;
      showErrorOnEmptyValue: boolean;
      field: keyof T;
      isAdvanced?: true;
    };

export interface ListPageHeaderProps<T extends InputForm = {}> {
  title: string;
  subtitle?: string;
  controls?: {
    form: T;
    onFormInput(update: any): void;
    inputs: InputProps<T>[];
    singleAction?: {
      label: string;
      Icon?: SvgIconComponent;
      onClick(): void;
      permission?: CheckMyPermissionDto;
      style?: React.CSSProperties;
    };
    menu?: {
      actions: LoadedActionsButtonItem[];
    };
  };
}

export function ListPageHeader<T extends InputForm>({ title, subtitle, controls }: ListPageHeaderProps<T>) {
  const theme = useTheme();
  const isSM = useMediaQuery(theme.breakpoints.down('sm'));
  const classes = useListPageHeaderStyles();

  const [showAdvanced, setShowAdvanced] = useImmer(false);
  const [touched, setTouched] = useImmer(false);
  const advancedInputs = controls?.inputs.filter((input) => input.isAdvanced);
  const hasActiveAdvancedInputs = advancedInputs?.some((input) => {
    if (input.type === 'text' || input.type === 'select') {
      return !_.isEmpty(controls?.form[input.field]);
    }
    if (input.type === 'date-range') {
      return !_.isEmpty(controls?.form[input.fromField]) || !_.isEmpty(controls?.form[input.toField]);
    }
    return false;
  });
  // Detect if advanced inputs are active on page load, and set to open if so
  useEffect(() => {
    if (!showAdvanced && !touched && hasActiveAdvancedInputs) {
      setShowAdvanced(true);
    }
  }, [showAdvanced, touched, hasActiveAdvancedInputs, setShowAdvanced]);

  return (
    <Grid
      className={classes.root}
      container
      wrap="nowrap"
      sx={{
        alignItems: 'flex-start',
        justifyContent: isSM ? 'flex-start' : 'space-between',
        flexDirection: isSM ? 'column' : 'row'
      }}
    >
      <Grid container sx={{ flexDirection: 'column' }} className={classes.title}>
        <Grid>
          <h2>{title}</h2>
        </Grid>
        {subtitle ? (
          <Grid>
            <p className={classes.subtitle}>{subtitle}</p>
          </Grid>
        ) : null}
      </Grid>
      {controls ? (
        <PageControlsWrapper form={controls?.form} onFormInput={controls.onFormInput}>
          {/* Show ALL controls on large screen, but only standard controls on small screen */}
          <Grid
            container
            style={isSM ? { maxWidth: '450px' } : {}}
            wrap={isSM ? 'nowrap' : 'wrap'}
            className={classes.controls}
            spacing={1}
            size="grow"
            sx={{
              alignItems: isSM ? 'flex-start' : 'center',
              justifyContent: isSM ? undefined : 'flex-end'
            }}
          >
            {controls.inputs.map((input, idx) => (
              <RenderInput key={idx} form={controls.form} input={input} showAdvanced={!isSM} />
            ))}

            {controls.singleAction ? (
              <Grid>
                {controls.singleAction.permission ? (
                  <PermissionBoundary permissions={[controls.singleAction.permission]}>
                    <ActionButtonSingle
                      onClick={controls.singleAction.onClick}
                      label={controls.singleAction.label}
                      Icon={controls.singleAction.Icon}
                      style={controls.singleAction.style}
                    />
                  </PermissionBoundary>
                ) : (
                  <ActionButtonSingle
                    onClick={controls.singleAction.onClick}
                    label={controls.singleAction.label}
                    Icon={controls.singleAction.Icon}
                  />
                )}
              </Grid>
            ) : null}
            {controls.menu ? (
              <Grid>
                <ActionsButton itemArray={controls.menu.actions} isMini />
              </Grid>
            ) : null}
          </Grid>
          {/* show advanced controls only on small screen and only if showAdvanced === true */}
          {advancedInputs && isSM ? (
            <Grid
              container
              spacing={1}
              style={{ maxWidth: '450px', marginTop: '4px' }}
              size={{
                xs: 12,
                md: 8
              }}
            >
              {advancedInputs.map((input, idx) => (
                <RenderInput key={idx} form={controls.form} input={input} showAdvanced={isSM && showAdvanced} />
              ))}
            </Grid>
          ) : null}
          {isSM ? (
            <Box
              className={classes.filtersToggle}
              onClick={() => {
                setShowAdvanced(!showAdvanced);
                setTouched(true);
              }}
            >
              {showAdvanced ? 'Hide' : 'Show'} advanced filters
            </Box>
          ) : null}
        </PageControlsWrapper>
      ) : null}
    </Grid>
  );
}

function RenderInput<T extends InputForm>({
  input,
  showAdvanced
}: {
  form: Exclude<ListPageHeaderProps<T>['controls'], undefined>['form'];
  input: InputProps<T>;
  showAdvanced: boolean;
}) {
  if (input.isAdvanced && showAdvanced === false) {
    return null;
  }
  if (input.type === 'text') {
    return (
      <Grid
        size={{
          xs: 12,
          md: 'auto'
        }}
      >
        <PageControlSearchInput name={input.field as string} placeholder={input.label} />
      </Grid>
    );
  }
  if (input.type === 'select') {
    return (
      <Grid
        size={{
          xs: input.halfWidthOnMobile ? 6 : 12,
          md: 'auto'
        }}
      >
        <PageControlDropdown name={input.field as string} placeholder={input.label} menuItems={input.options} />
      </Grid>
    );
  }
  if (input.type === 'auto-complete') {
    return (
      <Grid
        size={{
          xs: input.halfWidthOnMobile ? 6 : 12,
          md: 'auto'
        }}
      >
        <PageControlAutoComplete
          name={input.field as string}
          placeholder={input.label}
          options={input.options}
          onOpen={input.onOpen}
          loading={input.loading}
        />
      </Grid>
    );
  }
  if (input.type === 'multi-select') {
    return (
      <Grid
        size={{
          xs: 12,
          md: 'auto'
        }}
      >
        <MultiSelectPageControlDropdown
          name={input.field as string}
          showErrorOnEmptyValue={input.showErrorOnEmptyValue}
          placeholder={input.label}
          menuItems={input.options}
        />
      </Grid>
    );
  }
  if (input.type === 'date-range') {
    return (
      <Grid
        size={{
          xs: 12,
          md: 'auto'
        }}
      >
        <Grid
          container
          spacing={1}
          wrap="nowrap"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Grid size="grow">
            <PageControlDatePicker
              name={input.fromField as string}
              label={input.fromLabel}
              dateTransformer={(date) => date?.startOf('day') || null}
              fullWidth
              format="DD/MM/YYYY"
              disableFuture
              slots={{ openPickerIcon: Today }}
              slotProps={{
                openPickerIcon: { style: { color: '#E0E0E0', fontSize: 18 } },
                textField: {
                  variant: 'outlined'
                }
              }}
            />
          </Grid>
          <Grid>to</Grid>
          <Grid size="grow">
            <PageControlDatePicker
              name={input.toField as string}
              label={input.toLabel}
              dateTransformer={(date) => date?.endOf('day') || null}
              fullWidth
              format="DD/MM/YYYY"
              disableFuture
              slots={{ openPickerIcon: Today }}
              slotProps={{
                openPickerIcon: { style: { color: '#E0E0E0', fontSize: 18 } },
                textField: {
                  variant: 'outlined'
                }
              }}
            />
          </Grid>
        </Grid>
      </Grid>
    );
  }
  return null;
}
